import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ApiError, errorMessage, get } from '../../api.js';
import { Notice, initials } from '../../components/ui.js';
import { useSignedIn } from '../../session.js';
import { StatusBadges } from './StatusBadges.js';
import type { Page, RoleOption, UserSummary } from './types.js';

/** Administration > Users: search, status filter, pages of 20. */
export function Users() {
  const { can, refresh } = useSignedIn();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? '';
  const page = Number(params.get('page') ?? '1') || 1;
  const [search, setSearch] = useState(q);
  const [data, setData] = useState<Page<UserSummary> | null>(null);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const query = new URLSearchParams();
    if (q) query.set('q', q);
    if (status) query.set('status', status);
    query.set('page', String(page));
    let current = true;
    Promise.all([
      get<Page<UserSummary>>(`/admin/users?${query.toString()}`),
      get<RoleOption[]>('/admin/roles'),
    ])
      .then(([users, roleList]) => {
        if (!current) return;
        setData(users);
        setRoles(roleList);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) setError(errorMessage(err));
      });
    return () => {
      current = false;
    };
  }, [q, status, page, refresh]);

  // Search as the person types, without a history step per letter.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search.trim() === q) return;
      const next = new URLSearchParams(params);
      if (search.trim()) next.set('q', search.trim());
      else next.delete('q');
      next.delete('page');
      setParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [search, q, params, setParams]);

  const label = (key: string) => roles.find((r) => r.key === key)?.label ?? key;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const goTo = (n: number) => {
    const next = new URLSearchParams(params);
    next.set('page', String(n));
    setParams(next);
  };

  return (
    <>
      <Link to="/settings" className="back">
        Settings
      </Link>
      <div className="page-head">
        <h1 className="page">Users</h1>
        {can('admin.users.manage') && (
          <Link className="btn primary" to="/admin/users/new">
            Invite a user
          </Link>
        )}
      </div>
      <p className="lede">People who can sign in to the portal, and their roles.</p>
      <div className="toolbar">
        <input
          type="search"
          className="grow"
          aria-label="Search by name or email"
          placeholder="Search by name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          aria-label="Status"
          value={status}
          onChange={(e) => {
            const next = new URLSearchParams(params);
            if (e.target.value) next.set('status', e.target.value);
            else next.delete('status');
            next.delete('page');
            setParams(next);
          }}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="invited">Invited</option>
          <option value="deactivated">Deactivated</option>
        </select>
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      {data &&
        (data.items.length === 0 ? (
          <div className="empty">
            <strong>No user found</strong>
            Change the search or the status.
          </div>
        ) : (
          <ul className="list" aria-label="Users">
            {data.items.map((user) => (
              <li key={user.id}>
                <button
                  type="button"
                  className="row"
                  onClick={() => navigate(`/admin/users/${user.id}`)}
                >
                  <span className="av" aria-hidden="true">
                    {initials(user.firstName, user.lastName)}
                  </span>
                  <span className="tx">
                    <span className="t1">
                      {user.firstName} {user.lastName}
                    </span>
                    <span className="t2">{user.email}</span>
                  </span>
                  <span className="rt">
                    <span>{user.roles.map(label).join(', ')}</span>
                    <span className="badges">
                      <StatusBadges user={user} />
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ))}
      {data && pages > 1 && (
        <nav className="pager" aria-label="Pages">
          <button
            type="button"
            className="btn sm"
            disabled={page <= 1}
            onClick={() => goTo(page - 1)}
          >
            Previous
          </button>
          <span>
            Page {page} of {pages} ({data.total} users)
          </span>
          <button
            type="button"
            className="btn sm"
            disabled={page >= pages}
            onClick={() => goTo(page + 1)}
          >
            Next
          </button>
        </nav>
      )}
    </>
  );
}
